import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Field, AccessibleButton, useAccessibility } from '../lib/accessibility'
import { useAuthStore } from '../lib/store'
import type { Tenant } from '../lib/api'
import { MangoMark } from '../components/shared/MangoMark'
import { TropicalFronds } from '../components/shared/TropicalFronds'

// Shape of the auth response from the login / MFA-complete endpoints.
interface AuthPayload {
  access_token: string
  refresh_token?: string
  tenant?: Tenant | null
  tenant_id?: string | null
  role?: string | null
  status?: string | null
}

// WCAG 2.1 AA compliant login page with:
//   - Native email/password auth
//   - Google OAuth2 (Gmail + Workspace)
//   - Sign in with Apple (Apple ID)
//   - Keyboard navigable tabs (role=tablist)
//   - ARIA live regions for errors and success
//   - Focus management
//   - Minimum 44×44px touch targets
//   - No color-only error indicators
const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8080'

// Design tokens matching the app's dark theme
const C = {
  amber: '#F5A623',
  green: '#2D6A2F',
  text:  '#F0EDE4',
} as const

const NIL_UUID = '00000000-0000-0000-0000-000000000000'

// postLoginDest decides where to send a freshly authenticated user so they never
// land on the farm dashboard without a tenant (every tenant-scoped call would
// 401 and bounce them back to /login):
//   pending        → /pending     (awaiting admin approval)
//   superuser      → /admin       (staff console; no tenant by design)
//   no tenant yet  → /get-started (onboarding: apply to / join a tenant)
//   otherwise      → /            (their farm dashboard)
function postLoginDest(role?: string | null, tid?: string | null, status?: string | null): string {
  if (status === 'pending') return '/pending'
  if (role === 'superuser') return '/admin'
  const noTenant = !tid || tid === NIL_UUID
  if (noTenant || role === 'pending_tenant') return '/get-started'
  // Honor a ?next= deep link (e.g. scanning a field QR while logged out →
  // RequireAuth bounced here with ?next=/r/plant/123). Same-origin app paths
  // only ("/…", not "//host") to avoid an open-redirect.
  // Browsers normalize a backslash to a forward slash in a URL path, so "/\evil.com"
  // and "/\/evil.com" are protocol-relative and escape the origin — a "starts with /,
  // not //" check alone lets them through. Require a single leading slash followed by
  // a path character that can't start an authority.
  const next = new URLSearchParams(window.location.search).get('next')
  if (next && /^\/(?![/\\])/.test(next)) return next
  return '/'
}

export default function LoginPage() {
  const { announce } = useAccessibility()
  const setAuth = useAuthStore((s) => s.setAuth)
  const [mode, setMode] = useState<'login' | 'reset'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [resetSent, setResetSent] = useState(false)
  // Second-factor challenge after a correct password (backend returns HTTP 202):
  //   'otp'  → 6-digit code emailed (linked social login or failed-attempt burst)
  //   'totp' → code from the user's authenticator app
  const [challenge, setChallenge] = useState<null | 'otp' | 'totp'>(null)
  const [mfaToken, setMfaToken] = useState('')
  const [otpSentTo, setOtpSentTo] = useState('')
  const [code, setCode] = useState('')

  // Which social-login providers the backend has credentials for. Seed with
  // ['google'] (always configured) so email login + Google show with no flash;
  // the fetch adds Apple only once its credentials are set (e.g. after joining
  // the Apple Developer Program) — self-correcting, nothing to flip back on.
  const [oauthProviders, setOauthProviders] = useState<string[]>(['google'])
  useEffect(() => {
    let alive = true
    fetch(`${BASE}/api/v1/auth/providers`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d && Array.isArray(d.providers)) setOauthProviders(d.providers) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  // Preserve invite code from /join/:code flow through the OAuth redirect
  const inviteCode = new URLSearchParams(window.location.search).get('invite') ?? ''

  // Handle OAuth callback token in URL fragment (backend redirects here after OAuth)
  useEffect(() => {
    const fragment = new URLSearchParams(window.location.hash.slice(1))
    const token = fragment.get('token')
    if (token) {
      const role    = fragment.get('role')    ?? undefined
      const status  = fragment.get('status')  ?? 'active'
      const tid     = fragment.get('tid')
      const tenantRaw = fragment.get('tenant')
      const tenant  = tenantRaw ? JSON.parse(decodeURIComponent(tenantRaw)) : null
      const refresh = fragment.get('refresh')
      if (refresh) localStorage.setItem('refresh_token', refresh)
      setAuth(token, tenant, role, status)
      window.history.replaceState({}, '', window.location.pathname)
      // Hard navigation (not in-SPA) for the same reason as finishAuth: the
      // destination's data queries must not fire before the token is attached.
      // Route by role/tenant so a superuser/tenant-less user isn't bounced.
      window.location.assign(postLoginDest(role, tid, status))
    }
    const urlErr = new URLSearchParams(window.location.search).get('error')
    if (urlErr) {
      setErrors({ form: 'Sign-in was cancelled or failed. Please try again.' })
      announce('Sign-in failed. Please try again.', 'assertive')
      window.history.replaceState({}, '', window.location.pathname)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announce])

  function validate() {
    const e: Record<string, string> = {}
    if (!email.trim()) e.email = 'Email address is required'
    else if (!/\S+@\S+\.\S+/.test(email)) e.email = 'Enter a valid email address'
    if (mode !== 'reset') {
      if (!password) e.password = 'Password is required'
    }
    return e
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) {
      setErrors(errs)
      announce(`Form has ${Object.keys(errs).length} error${Object.keys(errs).length > 1 ? 's' : ''}. Please correct and try again.`, 'assertive')
      return
    }
    setErrors({})
    setLoading(true)
    try {
      if (mode === 'reset') {
        await fetch(`${BASE}/api/v1/auth/reset-request`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        })
        setResetSent(true)
        announce('Password reset email sent. Check your inbox.', 'polite')
        setLoading(false)
        return
      }
      const endpoint = `${BASE}/api/v1/auth/login`
      const body = { email, password, ...(inviteCode && { invite_code: inviteCode }) }
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      const data = await res.json()
      if (!res.ok) {
        setErrors({ form: data.error || 'Something went wrong. Please try again.' })
        announce(data.error || 'Sign-in failed.', 'assertive')
        return
      }
      // HTTP 202: password was correct but a second factor is required. No tokens
      // are issued yet — switch to the challenge screen to collect the code.
      if (data.requires_otp || data.requires_totp) {
        setChallenge(data.requires_otp ? 'otp' : 'totp')
        setMfaToken(data.mfa_token ?? '')
        setOtpSentTo(data.otp_sent_to ?? '')
        setCode('')
        announce(
          data.requires_otp
            ? `For your security, enter the verification code we emailed to ${data.otp_sent_to ?? 'your email'}.`
            : 'Enter the code from your authenticator app.',
          'assertive',
        )
        return
      }
      finishAuth(data)
    } catch {
      setErrors({ form: 'Network error. Please check your connection and try again.' })
      announce('Network error.', 'assertive')
    } finally {
      setLoading(false)
    }
  }

  // finishAuth stores tokens from a successful auth result and routes onward.
  // Uses a HARD navigation (window.location) rather than the in-SPA router:
  // the access token must be live in the persisted store and attached by the
  // axios request interceptor before the destination page fires its data
  // queries. An in-SPA navigate can let an early query fire tokenless, 401, and
  // trip the response interceptor's clearAuth()+redirect — bouncing the user
  // straight back to /login. A full load rehydrates the store first, so every
  // request carries the Bearer token.
  function finishAuth(data: AuthPayload) {
    const status = data.status ?? 'active'
    setAuth(data.access_token, data.tenant ?? null, data.role, status)
    if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token)
    // Route by role/tenant so a superuser/tenant-less user isn't bounced off the
    // dashboard. Hard navigation so queries fire only after the token attaches.
    window.location.assign(postLoginDest(data.role, data.tenant_id, status))
  }

  // handleChallengeSubmit completes the email-OTP or TOTP second factor.
  async function handleChallengeSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!code.trim()) {
      setErrors({ code: 'Enter the verification code' })
      announce('Verification code is required.', 'assertive')
      return
    }
    setErrors({})
    setLoading(true)
    try {
      const path = challenge === 'otp' ? 'mfa/email-complete' : 'mfa/complete'
      const payload =
        challenge === 'otp'
          ? { mfa_token: mfaToken, code: code.trim() }
          : { mfa_token: mfaToken, totp_code: code.trim() }
      const res = await fetch(`${BASE}/api/v1/auth/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (!res.ok) {
        setErrors({ code: data.error || 'Invalid or expired code. Please try again.' })
        announce(data.error || 'Verification failed.', 'assertive')
        return
      }
      finishAuth(data)
    } catch {
      setErrors({ code: 'Network error. Please check your connection and try again.' })
      announce('Network error.', 'assertive')
    } finally {
      setLoading(false)
    }
  }

  function cancelChallenge() {
    setChallenge(null)
    setMfaToken('')
    setCode('')
    setErrors({})
    setPassword('')
  }

  function startOAuth(provider: string) {
    announce(`Redirecting to ${provider} sign-in.`, 'polite')
    // Pass invite code through OAuth state so backend can process it post-callback
    const params = new URLSearchParams({ redirect_uri: window.location.origin + '/login' })
    if (inviteCode) params.set('invite_code', inviteCode)
    window.location.href = `${BASE}/api/v1/auth/oauth/${provider}/begin?${params}`
  }

  const sw = { border: 'none', padding: 0, margin: 0 } as const
  const sep: React.CSSProperties = { flex: 1, height: '0.5px', background: 'var(--color-border-tertiary)' }

  return (
    <main id="main-content" style={{
      minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      position: 'relative', overflow: 'hidden',
      background: 'radial-gradient(circle at 50% -10%, rgba(245,166,35,0.16), transparent 55%), radial-gradient(circle at 90% 110%, rgba(45,106,47,0.18), transparent 50%), var(--color-background-tertiary)',
    }}>
      <TropicalFronds />
      <div style={{ width: '100%', maxWidth: 400, position: 'relative' }}>
        {/* Logo */}
        <header style={{ textAlign: 'center', marginBottom: 36 }}>
          <a
            href="https://grafty.io"
            aria-label="Grafty — go to homepage"
            style={{ textDecoration: 'none', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}
          >
            <MangoMark size={72} />
            <span style={{
              fontSize: 32, fontWeight: 700, color: 'var(--color-text-primary)',
              fontFamily: '"Syne", system-ui, sans-serif', letterSpacing: '-0.02em',
              lineHeight: 1,
            }}>
              Grafty
            </span>
          </a>
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '8px 0 0' }}>
            Every tree, every harvest, every season.
          </p>
        </header>

        {/* New to Grafty link — replaces the old "Create account" tab */}
        {mode !== 'reset' && (
          <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 20, marginTop: 0 }}>
            New to Grafty?{' '}
            <Link to="/get-started" style={{ color: C.amber, textDecoration: 'none', fontWeight: 500 }}>
              Get started
            </Link>
          </p>
        )}

        {/* Card */}
        <section style={{ background: 'var(--color-background-primary)', border: '0.5px solid var(--color-border-tertiary)', borderRadius: 14, padding: 28 }}>
          <h1 style={{ fontSize: 18, fontWeight: 500, margin: '0 0 20px', textAlign: 'center' }}>
            {challenge ? 'Verify it’s you' : mode === 'login' ? 'Sign in to Grafty' : 'Reset your password'}
          </h1>

          {/* OAuth — only for login/register, hidden during a 2FA challenge, and
              only when at least one provider is actually configured */}
          {mode !== 'reset' && !challenge && oauthProviders.length > 0 && <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {/* Google */}
              {oauthProviders.includes('google') && (
              <button onClick={() => startOAuth('google')} aria-label="Continue with Google — opens Google sign-in"
                style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', minHeight: 44, background: 'var(--color-background-secondary)', border: '0.5px solid var(--color-border-secondary)', borderRadius: 8, padding: '0 16px', fontSize: 14, cursor: 'pointer', color: 'var(--color-text-primary)', width: '100%' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
              )}
              {/* Apple — hidden until APPLE_CLIENT_ID is configured (§200) */}
              {oauthProviders.includes('apple') && (
              <button onClick={() => startOAuth('apple')} aria-label="Continue with Apple — opens Apple sign-in"
                style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', minHeight: 44, background: '#000', border: 'none', borderRadius: 8, padding: '0 16px', fontSize: 14, cursor: 'pointer', color: '#fff', width: '100%' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white" aria-hidden="true" focusable="false">
                  <path d="M17.05 12.04c-.03-2.79 2.28-4.13 2.39-4.2-1.3-1.9-3.32-2.16-4.04-2.19-1.72-.17-3.36 1.01-4.23 1.01-.87 0-2.22-.99-3.65-.96-1.88.03-3.61 1.09-4.58 2.77-1.95 3.38-.5 8.39 1.4 11.14.93 1.35 2.04 2.86 3.5 2.81 1.4-.06 1.93-.9 3.63-.9 1.69 0 2.17.9 3.65.87 1.51-.03 2.46-1.37 3.38-2.73 1.07-1.57 1.51-3.09 1.53-3.17-.03-.01-2.94-1.13-2.97-4.47zM14.28 3.84c.77-.93 1.29-2.22 1.15-3.51-1.11.05-2.46.74-3.25 1.67-.71.82-1.33 2.14-1.16 3.4 1.24.1 2.5-.63 3.26-1.56z"/>
                </svg>
                Continue with Apple
              </button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={sep} aria-hidden="true" /><span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>or continue with email</span><div style={sep} aria-hidden="true" />
            </div>
          </>}

          {/* Error summary */}
          {errors.form && (
            <div role="alert" aria-live="assertive" style={{ background: 'var(--color-background-danger)', border: '0.5px solid var(--color-border-danger)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--color-text-danger)', marginBottom: 16, display: 'flex', gap: 8 }}>
              <span aria-hidden="true">⚠</span><span>{errors.form}</span>
            </div>
          )}
          {resetSent && (
            <div role="status" style={{ background: 'var(--color-background-success)', border: '0.5px solid var(--color-border-success)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--color-text-success)', marginBottom: 16 }}>
              Check your email for a password reset link.
            </div>
          )}

          {/* Second-factor (OTP / TOTP) challenge form */}
          {challenge && (
            <form onSubmit={handleChallengeSubmit} noValidate aria-label="Verification form">
              <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 16px', lineHeight: 1.5 }}>
                {challenge === 'otp'
                  ? <>For your security, we emailed a 6-digit code to <strong>{otpSentTo || 'your email'}</strong>. Enter it below to finish signing in.</>
                  : <>Enter the 6-digit code from your authenticator app.</>}
              </p>
              <fieldset style={sw}>
                <legend style={{ ...sw, fontSize: 0 }}>Verification code</legend>
                <Field label="Verification code" name="code" error={errors.code} required>
                  {ariaProps => (
                    <input
                      {...ariaProps}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={code}
                      onChange={e => setCode(e.target.value)}
                      placeholder="123456"
                      maxLength={8}
                      autoFocus
                      style={{ width: '100%', boxSizing: 'border-box', letterSpacing: '0.3em', fontSize: 18, textAlign: 'center' }}
                    />
                  )}
                </Field>
                <AccessibleButton type="submit" variant="primary" loading={loading} loadingLabel="Verifying…" style={{ width: '100%', marginTop: 8 }}>
                  Verify and sign in
                </AccessibleButton>
              </fieldset>
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <button type="button" onClick={cancelChallenge}
                  style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 13, cursor: 'pointer', minHeight: 44 }}>
                  ← Back to sign in
                </button>
              </div>
            </form>
          )}

          {/* Form — hidden during a 2FA challenge */}
          {!challenge && <>
          <form onSubmit={handleSubmit} noValidate aria-label={mode === 'login' ? 'Sign-in form' : 'Password reset form'}>
            <fieldset style={sw}>
              <legend style={{ ...sw, fontSize: 0 }}>{mode === 'login' ? 'Sign in with email' : 'Reset password'}</legend>
              <Field label="Email address" name="email" error={errors.email} required>
                {ariaProps => <input {...ariaProps} type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" placeholder="you@example.com" style={{ width: '100%', boxSizing: 'border-box' }} />}
              </Field>
              {mode !== 'reset' && (
                <Field label="Password" name="password" error={errors.password} required>
                  {ariaProps => <input {...ariaProps} type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" style={{ width: '100%', boxSizing: 'border-box' }} />}
                </Field>
              )}
              <AccessibleButton type="submit" variant="primary" loading={loading} loadingLabel="Signing in…" style={{ width: '100%', marginTop: 8 }}>
                {mode === 'login' ? 'Sign in' : 'Send reset link'}
              </AccessibleButton>
            </fieldset>
          </form>

          {/* Footer links */}
          <div style={{ marginTop: 16, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {mode === 'login' && (
              <button onClick={() => { setMode('reset'); setErrors({}) }} aria-label="Forgot your password? Click to reset it."
                style={{ background: 'none', border: 'none', color: 'var(--color-text-info)', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', minHeight: 44 }}>
                Forgot your password?
              </button>
            )}
            {mode === 'reset' && (
              <button onClick={() => { setMode('login'); setErrors({}); setResetSent(false) }}
                style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 13, cursor: 'pointer', minHeight: 44 }}>
                ← Back to sign in
              </button>
            )}
          </div>
          </>}
        </section>

        <p style={{ fontSize: 11, color: 'var(--color-text-tertiary)', textAlign: 'center', marginTop: 16, lineHeight: 1.6 }}>
          By continuing, you agree to our{' '}
          <a href="https://grafty.io/terms" style={{ color: 'var(--color-text-secondary)' }}>Terms of Service</a>
          {' '}and{' '}
          <a href="https://grafty.io/privacy" style={{ color: 'var(--color-text-secondary)' }}>Privacy Policy</a>.
        </p>
      </div>
    </main>
  )
}
